"use client";
import { useEffect, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import Image from "next/image";
import { FaGithub } from "react-icons/fa";
import moment from "moment";
import sanitizeHtml from "sanitize-html";

const backdropVariant = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.35 } },
  exit: { opacity: 0, transition: { duration: 0.3, delay: 0.1 } },
};

const panelVariant = {
  hidden: { y: "100%", opacity: 0.5 },
  show: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    y: "100%",
    opacity: 0,
    transition: { duration: 0.4, ease: [0.4, 0, 1, 1] },
  },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.25 } },
};

const item = {
  hidden: { y: 24, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

/* ─────────────────────────────────────────
   TECH PILL
───────────────────────────────────────── */
function TechPill({ label }) {
  return (
    <motion.span
      variants={item}
      whileHover={{ scale: 1.08, y: -2 }}
      className="inline-flex items-center px-3 py-1.5 rounded-full text-[10px] tracking-[0.15em] uppercase
        border border-white/[0.07] bg-white/[0.03] text-white/45
        hover:border-[#00f0ff]/40 hover:text-[#00f0ff]/80 hover:bg-[#00f0ff]/[0.05]
        transition-colors duration-300 cursor-default"
      style={{ fontFamily: "'DM Mono', monospace" }}
    >
      {label}
    </motion.span>
  );
}

/* ─────────────────────────────────────────
   FEATURE ITEM
───────────────────────────────────────── */
function FeatureItem({ text, index }) {
  return (
    <motion.div
      variants={item}
      className="flex items-start gap-3 p-4 rounded-xl
        border border-white/[0.05] bg-white/[0.02]
        hover:border-[#00f0ff]/20 hover:bg-[#00f0ff]/[0.03]
        transition-colors duration-300"
    >
      <span
        className="flex-shrink-0 text-[9px] tracking-[0.15em] text-[#00f0ff]/40 mt-0.5 font-medium"
        style={{ fontFamily: "'DM Mono', monospace" }}
      >
        {String(index + 1).padStart(2, "0")}
      </span>
      <span
        className="text-sm text-white/50 leading-relaxed"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        {text}
      </span>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   CHALLENGE ITEM
───────────────────────────────────────── */
function ChallengeItem({ text, index }) {
  return (
    <motion.div
      variants={item}
      className="flex items-start gap-3 py-3 border-b border-white/[0.04] last:border-0"
    >
      <span className="flex-shrink-0 text-[#00f0ff]/30 mt-1 text-xs">◈</span>
      <span
        className="text-sm text-white/45 leading-relaxed"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        {text}
      </span>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   SECTION LABEL
───────────────────────────────────────── */
function SectionLabel({ children }) {
  return (
    <motion.div variants={item} className="flex items-center gap-3 mb-5">
      <div className="h-px w-6 bg-gradient-to-r from-[#0047ff] to-[#00f0ff]" />
      <span
        className="text-[9px] tracking-[0.32em] uppercase text-[#00f0ff]/55"
        style={{ fontFamily: "'DM Mono', monospace" }}
      >
        {children}
      </span>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   MAIN MODAL
───────────────────────────────────────── */
export default function ProjectModal({ project, onClose }) {
  const scrollRef = useRef(null);

  /* Lock body scroll */
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  /* Close on Escape */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!project) return null;

  const {
    projects_name,
    thumbnail,
    short_description,
    skills,
    view_url,
    github_url,
    completion_date,
    duration,
    key_features,
    challenges,
    categoryId,
  } = project;

  const year = completion_date
    ? moment(completion_date).format("MMM YYYY")
    : "—";

  const cleanDesc = short_description
    ? sanitizeHtml(short_description, {
        allowedTags: ["p", "br", "strong", "em", "ul", "li"],
        allowedAttributes: {},
      })
    : "";

  return (
    <AnimatePresence>
      {/* ── Backdrop ── */}
      <motion.div
        key="backdrop"
        variants={backdropVariant}
        initial="hidden"
        animate="show"
        exit="exit"
        onClick={onClose}
        className="fixed inset-0 z-[200] bg-black/75 backdrop-blur-md"
      />

      {/* ── Modal Panel ── */}
      <motion.div
        key="panel"
        variants={panelVariant}
        initial="hidden"
        animate="show"
        exit="exit"
        className="fixed inset-x-0 bottom-0 z-[201] flex flex-col
          rounded-t-3xl overflow-hidden
          md:inset-4 md:rounded-3xl"
        style={{
          background: "#07070f",
          border: "1px solid rgba(255,255,255,0.05)",
          maxHeight: "95vh",
        }}
      >
        {/* Top gradient beam */}
        <div
          className="absolute top-0 left-0 right-0 h-px pointer-events-none z-10"
          style={{
            background:
              "linear-gradient(90deg, transparent, #0047ff, #00f0ff, transparent)",
          }}
        />

        {/* Corner brackets */}
        <svg
          className="absolute top-5 left-5 z-10 opacity-25"
          width="16"
          height="16"
          fill="none"
        >
          <path d="M0 16 L0 0 L16 0" stroke="#00f0ff" strokeWidth="1.2" />
        </svg>
        <svg
          className="absolute top-5 right-5 z-10 opacity-25"
          width="16"
          height="16"
          fill="none"
        >
          <path d="M16 16 L16 0 L0 0" stroke="#0047ff" strokeWidth="1.2" />
        </svg>

        {/* ── HEADER (sticky) ── */}
        <div
          className="flex-shrink-0 flex items-center justify-between px-6 md:px-8 py-5
            border-b border-white/[0.04]"
          style={{
            background: "rgba(7,7,15,0.95)",
            backdropFilter: "blur(12px)",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            {/* Category */}
            {categoryId?.category_name && (
              <span
                className="hidden sm:inline-flex px-2.5 py-1 rounded-full text-[9px] tracking-[0.2em] uppercase flex-shrink-0
                  border border-[#00f0ff]/15 bg-[#00f0ff]/[0.04] text-[#00f0ff]/60"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                {categoryId.category_name}
              </span>
            )}
            {/* Title */}
            <h2
              className="text-lg md:text-xl font-black text-white truncate"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              {projects_name || "Project Details"}
            </h2>
          </div>

          {/* Action buttons + close */}
          <div className="flex items-center gap-2 flex-shrink-0 ml-4 mt-20">
            {view_url && (
              <motion.a
                href={view_url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.94 }}
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full
                  text-[10px] tracking-[0.18em] uppercase text-[#05050b] font-semibold"
                style={{
                  background: "linear-gradient(135deg, #00f0ff, #0047ff)",
                  fontFamily: "'DM Mono', monospace",
                  boxShadow: "0 0 16px rgba(0,240,255,0.2)",
                }}
              >
                Live Demo ↗
              </motion.a>
            )}
            {github_url && (
              <motion.a
                href={github_url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.94 }}
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full
                  text-[10px] tracking-[0.18em] uppercase text-white/55
                  border border-white/[0.08] hover:border-white/20 hover:text-white/80
                  transition-colors duration-300"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                <FaGithub className="w-3.5 h-3.5" />
                GitHub
              </motion.a>
            )}

            {/* Close */}
            <motion.button
              onClick={onClose}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.22 }}
              className="w-9 h-9 rounded-full flex items-center justify-center
                border border-white/[0.08] bg-white/[0.03]
                hover:border-red-400/30 hover:bg-red-400/[0.06]
                transition-colors duration-300 text-white/50 text-lg leading-none ml-1"
            >
              ✕
            </motion.button>
          </div>
        </div>

        {/* ── SCROLLABLE CONTENT ── */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto overscroll-contain"
        >
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="px-6 md:px-8 py-8"
          >
            {/* ── HERO IMAGE ── */}
            {thumbnail && (
              <motion.div
                variants={item}
                className="relative w-full rounded-2xl overflow-hidden mb-8 group"
                style={{ aspectRatio: "16/8" }}
              >
                <Image
                  src={thumbnail}
                  alt={projects_name || "Project"}
                  fill
                  priority
                  className="object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  sizes="(max-width: 768px) 100vw, 80vw"
                />
                {/* Scan-line texture */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-20"
                  style={{
                    background:
                      "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.07) 3px, rgba(0,0,0,0.07) 4px)",
                  }}
                />
                {/* Bottom gradient */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(7,7,15,0.7) 0%, transparent 50%)",
                  }}
                />

                {/* Year + duration badges overlaid on image */}
                <div className="absolute bottom-4 left-4 flex items-center gap-2 z-10">
                  {year !== "—" && (
                    <span
                      className="px-3 py-1 rounded-full text-[9px] tracking-[0.2em] uppercase
                        border border-white/15 bg-[#07070f]/70 backdrop-blur-sm text-white/50"
                      style={{ fontFamily: "'DM Mono', monospace" }}
                    >
                      {year}
                    </span>
                  )}
                  {duration && (
                    <span
                      className="px-3 py-1 rounded-full text-[9px] tracking-[0.2em] uppercase
                        border border-[#00f0ff]/20 bg-[#07070f]/70 backdrop-blur-sm text-[#00f0ff]/60"
                      style={{ fontFamily: "'DM Mono', monospace" }}
                    >
                      ⏱ {duration}
                    </span>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── MOBILE CTA BUTTONS ── */}
            <motion.div variants={item} className="flex sm:hidden gap-3 mb-8">
              {view_url && (
                <a
                  href={view_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl
                    text-[10px] tracking-[0.18em] uppercase text-[#05050b] font-semibold"
                  style={{
                    background: "linear-gradient(135deg, #00f0ff, #0047ff)",
                    fontFamily: "'DM Mono', monospace",
                  }}
                >
                  Live Demo ↗
                </a>
              )}
              {github_url && (
                <a
                  href={github_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl
                    text-[10px] tracking-[0.18em] uppercase text-white/55
                    border border-white/[0.08]"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  <FaGithub className="w-3.5 h-3.5" />
                  GitHub
                </a>
              )}
            </motion.div>

            {/* ── TWO-COLUMN LAYOUT ── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* LEFT — main content (2/3) */}
              <div className="lg:col-span-2 space-y-10">
                {/* Overview */}
                {cleanDesc && (
                  <div>
                    <SectionLabel>Project Overview</SectionLabel>
                    <motion.div
                      variants={item}
                      className="text-sm md:text-base text-white/45 leading-relaxed prose prose-invert max-w-none
                        prose-p:text-white/45 prose-strong:text-white/70 prose-li:text-white/40"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                      dangerouslySetInnerHTML={{ __html: cleanDesc }}
                    />
                  </div>
                )}

                {/* Key Features */}
                {key_features?.length > 0 && (
                  <div>
                    <SectionLabel>Key Features</SectionLabel>
                    <motion.div
                      variants={stagger}
                      initial="hidden"
                      animate="show"
                      className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                    >
                      {key_features.map((f, i) => (
                        <FeatureItem key={i} text={f} index={i} />
                      ))}
                    </motion.div>
                  </div>
                )}

                {/* Challenges */}
                {challenges?.length > 0 && (
                  <div>
                    <SectionLabel>Challenges & Solutions</SectionLabel>
                    <motion.div
                      variants={stagger}
                      initial="hidden"
                      animate="show"
                      className="rounded-xl border border-white/[0.05] bg-white/[0.015] px-5 py-2"
                    >
                      {challenges.map((c, i) => (
                        <ChallengeItem key={i} text={c} index={i} />
                      ))}
                    </motion.div>
                  </div>
                )}
              </div>

              {/* RIGHT — sidebar (1/3) */}
              <div className="space-y-8">
                {/* Tech stack */}
                {skills?.length > 0 && (
                  <div>
                    <SectionLabel>Tech Stack</SectionLabel>
                    <motion.div
                      variants={stagger}
                      initial="hidden"
                      animate="show"
                      className="flex flex-wrap gap-2"
                    >
                      {skills.map((s, i) => (
                        <TechPill key={i} label={s} />
                      ))}
                    </motion.div>
                  </div>
                )}

                {/* Project meta */}
                <div>
                  <SectionLabel>Project Info</SectionLabel>
                  <motion.div
                    variants={stagger}
                    initial="hidden"
                    animate="show"
                    className="flex flex-col gap-3"
                  >
                    {[
                      { glyph: "◈", label: "Completed", value: year },
                      { glyph: "◉", label: "Duration", value: duration || "—" },
                      {
                        glyph: "◎",
                        label: "Category",
                        value: categoryId?.category_name || "—",
                      },
                      {
                        glyph: "◐",
                        label: "Stack",
                        value: `${skills?.length || 0} technologies`,
                      },
                    ].map(({ glyph, label, value }) => (
                      <motion.div
                        key={label}
                        variants={item}
                        className="flex items-center gap-3 py-3 px-4 rounded-lg
                          border border-white/[0.04] bg-white/[0.015]"
                      >
                        <span className="text-[#00f0ff]/35 text-sm flex-shrink-0">
                          {glyph}
                        </span>
                        <div className="min-w-0">
                          <div
                            className="text-[9px] tracking-[0.22em] text-white/20 uppercase mb-0.5"
                            style={{ fontFamily: "'DM Mono', monospace" }}
                          >
                            {label}
                          </div>
                          <div
                            className="text-sm text-white/60 truncate"
                            style={{ fontFamily: "'DM Sans', sans-serif" }}
                          >
                            {value}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                </div>

                {/* CTA repeat for sidebar */}
                <motion.div
                  variants={item}
                  className="flex flex-col gap-3 hidden lg:flex"
                >
                  {view_url && (
                    <motion.a
                      href={view_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      className="relative group w-full flex items-center justify-center gap-2
                        py-3 rounded-xl text-[10px] tracking-[0.18em] uppercase
                        text-[#05050b] font-semibold overflow-hidden"
                      style={{
                        background: "linear-gradient(135deg, #00f0ff, #0047ff)",
                        fontFamily: "'DM Mono', monospace",
                        boxShadow: "0 0 20px rgba(0,240,255,0.18)",
                      }}
                    >
                      <span className="relative z-10">View Live ↗</span>
                      <span
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(255,255,255,0.2), transparent)",
                        }}
                      />
                    </motion.a>
                  )}
                  {github_url && (
                    <motion.a
                      href={github_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      className="w-full flex items-center justify-center gap-2
                        py-3 rounded-xl text-[10px] tracking-[0.18em] uppercase
                        text-white/50 border border-white/[0.08]
                        hover:border-[#00f0ff]/25 hover:text-[#00f0ff]/70
                        transition-all duration-300"
                      style={{ fontFamily: "'DM Mono', monospace" }}
                    >
                      <FaGithub className="w-3.5 h-3.5" />
                      Source Code
                    </motion.a>
                  )}
                </motion.div>
              </div>
            </div>

            {/* Bottom padding */}
            <div className="h-8" />
          </motion.div>
        </div>

        {/* Bottom gradient fade */}
        <div
          className="flex-shrink-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, #0047ff30, #00f0ff20, transparent)",
          }}
        />
      </motion.div>
    </AnimatePresence>
  );
}
